import { c as _c } from "react/compiler-runtime";
import { useDidMount } from '@7f-ui/hooks/use-did-mount';
import type { NexperienceVariantFragment as NexperienceVariant } from '@data-access/lib/gql/cart/documents';
import { dateParser } from '@feat/feat-url-state/date';
import { useUrlState } from '@feat/feat-url-state/use-url-state';
import { objectKeys } from '@utils/helpers';
import { pick } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';
import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useState } from 'react';
import { initSelectedOptions, selectedOptionsParser } from '../_lib/experience-booking-utils';
import { normalizeExperienceInterval as getInterval } from '../_lib/experience-date-range';
import type { ExperienceFilter } from '../_lib/experience-state-types';
import { normalizeInitialExperienceFilter } from '../_lib/normalize-initial-experience-filter';
import { useExperienceOptions } from './use-app-experience-options';
type UseExperienceFilter = {
  urlStateEnabled?: boolean;
  initialFilter?: ExperienceFilter;
  variants?: NexperienceVariant[];
};
const parsers = {
  selectedVariant: parseAsString,
  selectedOptions: selectedOptionsParser
};
export const useExperienceFilterState = t0 => {
  const $ = _c(23);
  const {
    urlStateEnabled,
    initialFilter,
    variants
  } = t0;
  const {
    allOptions
  } = useExperienceOptions(variants);
  const {
    urlState,
    updateUrlState
  } = useUrlState();
  let t1;
  if ($[0] !== urlState) {
    t1 = pick(urlState, objectKeys(dateParser));
    $[0] = urlState;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dateRange = t1;
  const [queryParams, setQueryParams] = useQueryStates(parsers);
  let t2;
  if ($[2] !== allOptions || $[3] !== dateRange || $[4] !== initialFilter || $[5] !== queryParams || $[6] !== variants) {
    t2 = () => {
      const mergedFilter = {
        ...queryParams,
        ...dateRange,
        ...initialFilter
      };
      const normalizedFilter = variants ? normalizeInitialExperienceFilter(mergedFilter, {
        variants
      }) : mergedFilter;
      let {
        selectedVariant: t3,
        selectedOptions
      } = normalizedFilter;
      let selectedVariant;
      selectedVariant = t3;
      const {
        from,
        to
      } = dateRange;
      if (!selectedVariant) {
        selectedVariant = variants?.[0]?.id ?? "";
      }
      if (!selectedOptions || isEmpty(selectedOptions)) {
        const variantOptions = allOptions.filter(t4 => {
          const {
            variantId
          } = t4;
          return selectedVariant === variantId;
        });
        selectedOptions = initSelectedOptions(variantOptions);
      }
      const interval = getInterval({
        from,
        to
      });
      return {
        ...normalizedFilter,
        selectedVariant,
        selectedOptions,
        ...interval
      };
    };
    $[2] = allOptions;
    $[3] = dateRange;
    $[4] = initialFilter;
    $[5] = queryParams;
    $[6] = variants;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const [filter, setFilter] = useState(t2);
  let t3;
  if ($[8] !== setQueryParams || $[9] !== updateUrlState || $[10] !== urlStateEnabled) {
    t3 = async t4 => {
      const {
        selectedVariant: selectedVariant_0,
        selectedOptions: selectedOptions_0,
        from: from_0,
        to: to_0
      } = t4;
      const interval_0 = getInterval({
        from: from_0,
        to: to_0
      });
      if (urlStateEnabled) {
        updateUrlState({
          ...interval_0
        });
        await setQueryParams({
          selectedVariant: selectedVariant_0,
          selectedOptions: selectedOptions_0
        });
      }
      setFilter({
        selectedVariant: selectedVariant_0,
        selectedOptions: selectedOptions_0,
        ...interval_0
      });
    };
    $[8] = setQueryParams;
    $[9] = updateUrlState;
    $[10] = urlStateEnabled;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const updateFilter = t3;
  let t4;
  if ($[12] !== filter || $[13] !== setQueryParams || $[14] !== urlStateEnabled) {
    t4 = () => {
      const {
        selectedVariant: selectedVariant_1,
        selectedOptions: selectedOptions_1
      } = filter;
      if (urlStateEnabled) {
        setQueryParams({
          selectedVariant: selectedVariant_1,
          selectedOptions: selectedOptions_1
        });
      }
    };
    $[12] = filter;
    $[13] = setQueryParams;
    $[14] = urlStateEnabled;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  useDidMount(t4);
  let t5;
  if ($[16] !== dateRange || $[17] !== filter || $[18] !== urlStateEnabled) {
    t5 = urlStateEnabled ? {
      ...filter,
      ...getInterval(dateRange)
    } : filter;
    $[16] = dateRange;
    $[17] = filter;
    $[18] = urlStateEnabled;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const effectiveFilter = t5;
  let t6;
  if ($[20] !== effectiveFilter || $[21] !== updateFilter) {
    t6 = {
      filter: effectiveFilter,
      setFilter: updateFilter
    };
    $[20] = effectiveFilter;
    $[21] = updateFilter;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};